import styled from 'styled-components';
import { z } from 'zod';
import {
  ButtonContainer,
  ErrorWrapper,
  InputWrapper,
  Loading,
  Modal,
  PrimaryButton,
  SecondaryButton,
  SectionHeading,
  SectionPreTitle,
  SectionTitle,
  SectionWrapper
} from '../atoms';
import { zodResolver } from '@hookform/resolvers/zod';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { APIEndPoints, Request } from '../../apis';
import { Logger } from '../../common';
import { toast } from 'react-hot-toast';

type TNewUserPlatformModal = {
  isModalOpen: boolean;
  handleModalClose: () => void;
  refreshPage: () => void;
};

const FormFooter = styled((props) => <ButtonContainer {...props} />)`
  margin-top: 45px;
`;

type TCreateUserRequest = {
  email: string;
  mobile: string;
  name: string;
  title: string;
  temporaryPassword: string;
};

const newUserFormSchema = z.object({
  fullName: z
    .string()
    .regex(
      /^[a-zA-Z]{1,}( {1,2}[a-zA-Z]{1,}){0,}$/,
      'Please enter a valid full name.'
    ),
  title: z.string().min(1, 'Title must be at least 1 character.'),
  email: z.string().email('Please enter a valid email address.'),
  mobile: z.string().regex(/^04\d{8}$/, 'Please enter a valid mobile.'),
  temporaryPassword: z
    .string()
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+~`|}{[\]\\:;?><,./-=])[A-Za-z0-9!@#$%^&*()_+~`|}{[\]\\:;?><,./-=]{8,}$/,
      'Temporary password must include one uppercase and lowercase letter, number, special character and be at least 8 characters long.'
    )
});

type NewUserFormScheme = z.infer<typeof newUserFormSchema>;

const NewUserPlatformModal = ({
  isModalOpen,
  handleModalClose,
  refreshPage
}: TNewUserPlatformModal) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<NewUserFormScheme>({
    resolver: zodResolver(newUserFormSchema)
  });

  const [creating, setCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const onSubmit: SubmitHandler<NewUserFormScheme> = async (data: any) => {
    setErrorMessage('');

    const { email, fullName, mobile, temporaryPassword, title } = data;

    try {
      const createUserRequest: TCreateUserRequest = {
        temporaryPassword: temporaryPassword,
        title: title,
        name: fullName,
        mobile: mobile,
        email: email
      };

      setCreating(true);
      await Request.post(
        APIEndPoints.usersPlatform,
        z.any(),
        createUserRequest
      );
      Logger.info({ source: 'NewUserPlatformModal', message: 'User created' });
      setCreating(false);
      toast.success('User created.');
      reset();
      refreshPage();
      handleModalClose();
    } catch (err: any) {
      setErrorMessage(err.message);
      setCreating(false);
    }
  };

  return (
    <Modal
      handleClose={handleModalClose}
      isModalVisible={isModalOpen}
      content={
        <>
          <SectionPreTitle>User</SectionPreTitle>
          <SectionTitle>Create New Platform User</SectionTitle>
          <form onSubmit={handleSubmit(onSubmit)} noValidate={true}>
            <SectionWrapper>
              <SectionHeading>Login Details</SectionHeading>
              <InputWrapper
                name="fullName"
                type="text"
                label="Full Name"
                required
                register={register}
                errorMessage={errors.fullName?.message}
              />
              <InputWrapper
                name="title"
                type="text"
                label="Title"
                required
                register={register}
                errorMessage={errors.title?.message}
              />
              <InputWrapper
                name="email"
                type="email"
                label="Email"
                required
                autoFill={false}
                register={register}
                errorMessage={errors.email?.message}
              />
              <InputWrapper
                name="mobile"
                type="text"
                label="Mobile"
                required
                register={register}
                errorMessage={errors.mobile?.message}
              />
              <InputWrapper
                name="temporaryPassword"
                type="password"
                label="Temporary Password"
                required
                autoFill={false}
                register={register}
                errorMessage={errors.temporaryPassword?.message}
              />
            </SectionWrapper>

            {errorMessage && (
              <div>
                <ErrorWrapper>{errorMessage}</ErrorWrapper>
              </div>
            )}
            <SectionWrapper>
              <FormFooter
                horizontalSpacing="10px"
                verticalSpacing="0px"
                justify="end"
              >
                {creating ? (
                  <Loading />
                ) : (
                  <>
                    <SecondaryButton
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleModalClose();
                      }}
                    >
                      Cancel
                    </SecondaryButton>
                    <PrimaryButton type="submit" disabled={creating}>
                      Create User
                    </PrimaryButton>
                  </>
                )}
              </FormFooter>
            </SectionWrapper>
          </form>
        </>
      }
    />
  );
};

export { NewUserPlatformModal };
